import { DataTableStateEvent } from 'primereact/datatable';
import { QueryClient } from 'react-query';

export enum _HttpMethods {
    GET = 'get',
    POST = 'post',
    PUT = 'put',
    DELETE = 'delete',
}

export type _UserType = 'superAdmin' | 'adminStaff' | 'organizationAdmin' | 'organizationStaff';

export type Permission = {
    id: string;
    parentPermissionId: string | null;
    permissionId: string | null;
    moduleId: string;
    permissionKey: string;
    permissionName: string;
    permissionDescription?: string;
    isActive: boolean;
    isDefault: boolean;
    permissions: Permission[];
};

export type RolePermissions = {
    id: string;
    permissionRoleName: string;
    permissionRoleDescription: string;
    permissionKey: string;
    isActive: boolean;
    isDefault: boolean;
    tenantId: string;
    permissions: Permission[];
    permissionRoleId?: string;
};

export type _User = {
    activeAdmins: [];
    createdAt: string;
    email: string;
    firstName: string;
    hireDate: string;
    id: string;
    isActive: boolean;
    lastName: string;
    loginUserName: string;
    passwordLessLoginOnly: boolean;
    phoneNumber: string;
    photoKey: string;
    photoThumbnail: string;
    preferredLangKey: string;
    preferredName: string;
    preferredUserName: string;
    sendPasswordCreation: false;
    signedUrl: string;
    staffPermissionRoles: RolePermissions[];
    updatedAt: string;
    userId: string;
    venueCount: number;
    organizations?: _AdminOrganizationDetails[];
    organizationName?: string;
};

type _AdminOrganizationDetails = {
    organizationId: string;
    organizationName: string;
    contactDetails: Record<string, any>;
    invitationStatus: _InvitationStatus;
    isActive: boolean;
    metaData: {
        venueCount: number;
    };
    venueCount: number;
    staffPermissionRoles: RolePermissions[];
};

export enum _InvitationStatus {
    PENDING = 'pending',
    ACCEPTED = 'accepted',
    DECLINED = 'declined',
}

export type _Organization = {
    address: string;
    city: string;
    contactName: string;
    country: string;
    emailId: string;
    id: string;
    invitationStatus: _InvitationStatus;
    isActive: boolean;
    organizationName: string;
    primaryPhone: string;
    secondaryPhone: string;
    state: string;
    zip: string;
    website: string;
    userName: string;
    registrationDate: string;
};

export type _Pagination = {
    currentPage: number;
    from: number;
    lastPage: number;
    limit: number;
    nextPageUrl: string;
    path: string;
    prevPageUrl: string | null;
    to: number;
    total: number;
};

export type _DatatablePageEvent = DataTableStateEvent & {
    rowsPerPageOptions: number[];
    forceUpdate: number;
    paginatorTemplate: string;
    search: string;
    totalRows: number;
};

export type _Payload = {
    queryClient?: QueryClient;
    url: string;
    method?: _HttpMethods;
    noAuth?: boolean;
    headers?: {};
    data?: {};
    isFile?: boolean;
    isSignedUrl?: boolean;
    xTenantId?: string;
};

export type _ErrorMessage = {
    data: {
        message: string;
        errors: [
            {
                key: string;
                message: string;
                param: string;
            },
        ];
        details: string;
    };
};

export type _ToastMessageProps = {
    message: string;
    variant: string;
    show: boolean;
};

export type _ToastMessageVariant = 'success' | 'info' | 'warn' | 'error' | undefined;

export type _TypeCase = 'upper' | 'lower' | 'capitalize' | 'pascal' | 'none';

export type _Country = {
    name: string;
    code: string;
    langKey: string;
    langName: string;
};

export type _GoogleAddress = {
    city_town: string | undefined;
    country_region: string | undefined;
    state: string | undefined;
    zip_code: string | undefined;
    lat: number | undefined;
    lng: number | undefined;
    place_id: string | undefined;
    formatted_address: string | undefined;
};

export type _S3ImageResponse = {
    imageUrl: string | null;
    imageKey: string | null;
};

export type _CropType =
    | 'LANDSCAPE'
    | 'PORTRAIT'
    | 'BANNER'
    | 'MENU'
    | 'BEVERAGE'
    | 'FOOD'
    | 'INGREDIENT'
    | 'NEWS'
    | 'EVENTS'
    | 'MOBILE_LOGO'
    | 'WEB_LOGO'
    | 'PROFILE_IMAGE';

export type _ImageType = {
    [key in _CropType]: {
        width: number;
        height: number;
        aspectRatio: number;
    };
};

export type _Permission = {
    id: string;
    parentPermissionId: string;
    permissionKey: string;
    permissionName: string;
    defaultValue: boolean;
    permissionDescription: string;
    isActive: boolean;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
    parent_permission_id: string;
    subPermissions: _Permission[];
    moduleName?: string;
};

export type _SubGroupPermission = {
    id: string;
    isActive: boolean;
    isDefault: boolean;
    parentPermissionKey: string;
    permissionKey: string;
    permissionName: string;
    defaultPermissionId: string;
    permissionId?: string;
};

export type _GroupPermission = {
    id?: string;
    defaultPermissionId: string;
    permissionName: string;
    permissionKey: string;
    parentPermissionKey: string;
    isActive: boolean;
    isDefault: boolean;
    subPermissions: _SubGroupPermission[];
};

// slim permission group row from the */permission_group/options endpoints
export type _PermissionGroupOption = {
    id: string;
    permissionRoleName: string;
    isDefault: boolean;
};

export type _PermissionGroup = {
    id: string;
    permissionRoleName: string;
    permissionRoleDescription: string;
    isActive: boolean;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
    permissions: _GroupPermission[];
};

export type _AdminStaffOrganizationDetailsInForm = {
    organizationName: string;
    id: string;
};

export type _AdminStaffPermissionGroupDetailsInForm = {
    permissionRoleName: string;
    id: string;
};

// slim organization row from /admin/organization/options
export type _OrganizationOption = {
    id: string;
    organizationName: string;
    isActive: boolean;
};

export type _StaffOrganizationForm = {
    organization: _AdminStaffOrganizationDetailsInForm;
    permissionGroup: _AdminStaffPermissionGroupDetailsInForm;
};

export type _AdminStaffForm = {
    username: string;
    displayName: string;
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    hireDate: string;
    permissionGroup: string;
    organizations: _StaffOrganizationForm[];
};

type _AdminStaffOrganizationData = {
    organizationId: string;
    staffPermissionRoles: string[]; // Array of role IDs
};

export type _AdminStaffData = {
    loginUserName: string;
    password: string;
    preferredName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    hireDate: string;
    staffPermissionRoles: string[];
    photoKey: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    sendPasswordCreationTo: 'phone' | 'email';
    userType: _UserType;
    organizations?: _AdminStaffOrganizationData[];
    id?: string; // for editing staff
};

export type _AdminStaff = {
    id: string;
    userId: string;
    loginUserName: string;
    preferredUserName: string;
    email: string;
    firstName: string;
    lastName: string;
    preferredName: string;
    hireDate: string | null;
    phoneNumber: string;
    photoKey: string;
    photoThumbnail: string | null;
    preferredLangKey: string;
    signedUrl: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    userType: 'superAdmin' | 'staff' | 'admin';
    isActive: boolean;
    staffPermissionRoles: {
        id: string;
        permissionRoleName: string;
    }[];
    activeStaffs: {
        id: string;
        name: string;
    }[]; // Assuming this is an array of staff info, adjust if needed
};

export type _OrgStaff = {
    id: string;
    userId: string;
    loginUserName: string;
    preferredUserName: string;
    email: string;
    firstName: string;
    lastName: string;
    preferredName: string;
    hireDate: string | null;
    phoneNumber: string;
    photoKey: string;
    photoThumbnail: string | null;
    preferredLangKey: string;
    signedUrl: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    userType: 'organizationAdmin' | 'organizationStaff';
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    staffPermissionRoles: {
        id: string;
        permissionRoleId: string;
        associableType: 'OrganizationStaff' | string; // If more types exist, expand this union
        associableId: string;
        createdAt: string; // ISO date string
        updatedAt: string; // ISO date string
        organizationPermissionRoleId: string;
        permissionRoleName: string;
    }[];
    activeStaffs: {
        id: string;
        name: string;
    }[]; // Assuming this is an array of staff info, adjust if needed
};

type _OrgStaffVenueData = {
    tenantId: string;
    staffPermissionRoles: string[];
    jobRoles?: string[];
};

export type _OrgStaffData = {
    loginUserName: string;
    password: string;
    preferredName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    hireDate: string | null;
    staffPermissionRoles: string[];
    photoKey: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    sendPasswordCreationTo: 'phone' | 'email';
    userType: _UserType;
    venues?: _OrgStaffVenueData[];
    id?: string; // for editing staff
};

export type _OrgStaffVenueDetailsInForm = {
    venueName: string;
    id: string;
};

export type _OrgStaffPermissionGroupDetailsInForm = {
    permissionRoleName: string;
    id: string;
};

export type _StaffVenueForm = {
    venue: _OrgStaffVenueDetailsInForm;
    permissionGroup: _OrgStaffPermissionGroupDetailsInForm;
};

export type _Venue = {
    id: string;
    tenantName: string;
    isActive: boolean;
    contactName: string;
    email: string;
    primaryPhone: string;
    secondaryPhone: string;
    address: string;
    city: string;
    zip: string;
    state: string;
    country: string;
    domainName: string;
    subDomainStatus: 'COMPLETED' | 'PENDING';
};

// slim venue row from /organization/venue/options (note: API spells it `subdomainStatus`)
export type _VenueOption = {
    id: string;
    tenantName: string;
    isActive: boolean;
    subdomainStatus: 'COMPLETED' | 'PENDING';
};

// shape the venue pickers hold in state
export type _VenuePickerOption = {
    id: string;
    venueName: string;
    isActive: boolean;
};

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export type _ThemeColorsType = {
    primary?: string;
    secondary?: string;
    tertiary?: string;
    text?: string;
};

// Subscription plan related types
export type _SubscriptionPlan = {
    id: string;
    planName: string;
    subscriberCount: number;
    pricing: number;
    pricingPeriod: 'Month' | 'Year';
    features: string;
    status: 'Active' | 'Inactive';
    isActive: boolean;
};

export type _AddSubscriptionPlanFormType = {
    id?: string;
    planName: string;
    planIcon: string;
    description: string;
    pricing: number;
    pricingPeriod: 'Monthly' | 'Yearly';
    features: string[];
    isRecommended: boolean;
};
